import { IoMdClose } from "react-icons/io";
import { Flex } from "../Flex";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { StatusBadge } from "../StatusBadge";
import { ViewSchema } from "../../hooks/dashboardViews/types";

export interface ActiveFilterBarProps {
  appliedKeys: string[];
  schema: ViewSchema;
  view: Record<string, unknown>;
  optionLists?: Record<string, unknown[]>;
  onRemove: (key: string) => void;
  onClearAll: () => void;
}

/**
 * A field renders a chip whenever its schema declares chipLabel - that
 * covers both filter-panel fields and something like an assigned-user
 * selector that lives in the page's topbar. The Filter button's own count
 * is narrower (see getAppliedFilterCount below): it additionally requires
 * inFilterPanel, since a chip outside the panel should still be visible
 * and removable, but must not inflate a count that's meant to describe
 * what's inside that specific panel.
 */
const chipLabelFor = (
  field: ViewSchema["fields"][string],
  value: unknown,
  options: unknown[]
): string | null => {
  if (!field?.chipLabel) return null;
  try {
    return field.chipLabel(value, options);
  } catch {
    // A chip label that fails to resolve (e.g. an option list mid-load)
    // is dropped rather than crashing the page; appliedKeys already
    // guarantees the underlying value itself is safe.
    return null;
  }
};

/**
 * Reusable by each dashboard's Filter button. Narrower than the chip row:
 * only fields the panel itself controls count, so a topbar selector like
 * assigned-user can show a chip without inflating this badge.
 */
export const getAppliedFilterCount = (
  appliedKeys: string[],
  schema: ViewSchema
): number =>
  appliedKeys.filter((key) => {
    const field = schema.fields[key];
    return Boolean(field?.chipLabel) && Boolean(field?.inFilterPanel);
  }).length;

export const ActiveFilterBar = ({
  appliedKeys,
  schema,
  view,
  optionLists = {},
  onRemove,
  onClearAll,
}: ActiveFilterBarProps) => {
  const chips = appliedKeys
    .map((key) => {
      const field = schema.fields[key];
      const options = field?.optionsKey ? optionLists[field.optionsKey] ?? [] : [];
      const label = chipLabelFor(field, view[key], options);
      return label === null ? null : { key, label };
    })
    .filter((chip): chip is { key: string; label: string } => chip !== null);

  // Nothing applied that carries a chip: render nothing at all, not even
  // an empty wrapper, so a default dashboard has no reserved empty space
  // and is pixel-identical to the current release.
  if (chips.length === 0) return null;

  return (
    <Flex flexWrap="wrap" alignItems="center" gap="8" py={2} aria-label="Applied filters">
      {chips.map(({ key, label }) => (
        <StatusBadge key={key} intent="neutral" withDot={false}>
          <Flex alignItems="center" gap="4">
            <span style={{ textTransform: "none" }}>{label}</span>
            <button
              type="button"
              onClick={() => onRemove(key)}
              aria-label={`Remove filter: ${label}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <Icon Type={IoMdClose} size="14px" color="gray.60" />
            </button>
          </Flex>
        </StatusBadge>
      ))}
      <Button variant="text" size="small" onClick={onClearAll}>
        Clear all
      </Button>
    </Flex>
  );
};
